import { cn } from '../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../styling/stylingHelper';
import type {
  EmailAccuracyAuditResult,
  ProcessedEmailResult,
  NotProcessedEmailResult,
} from '../../../../../services/admin/adminService';
import { AuditScoreReport, type ReviewBucket } from '../../AuditReview';
import { KV, MsgIdCopyCell } from '../shared/copyCells';
import type { TFn } from '../types';

const PROCESSED_VERDICT_STYLES: Record<ProcessedEmailResult['verdict'], string> = {
  correct: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  wrong: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  not_checked: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
};

const NOT_PROCESSED_VERDICT_STYLES: Record<NotProcessedEmailResult['verdict'], string> = {
  needs_check: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  good_skip: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
};

// Card body for one email under review (wrong total or needs-check).
function EmailCardBody({ e, t }: { e: ProcessedEmailResult | NotProcessedEmailResult; t: TFn }) {
  const reason = 'reason' in e ? e.reason : '';
  const storedTotal = 'storedTotal' in e ? e.storedTotal : null;
  const issues = 'issues' in e ? e.issues : [];
  return (
    <div className="space-y-2 text-xs">
      <KV label={t('adminTesting.review.labelFrom')} value={e.from} />
      <KV label={t('adminTesting.review.labelSubject')} value={e.subject} />
      {storedTotal != null && <KV label={t('adminTesting.review.labelStoredTotal')} value={`€${storedTotal.toFixed(2)}`} />}
      {reason && <KV label={t('adminTesting.review.labelReason')} value={reason} />}
      {issues.length > 0 && (
        <ul className="list-disc pl-5 mt-1 space-y-1 text-red-600 dark:text-red-400">
          {issues.map((iss, i) => <li key={i}>{iss}</li>)}
        </ul>
      )}
    </div>
  );
}

// Build review buckets from an email accuracy result: wrong totals, new suppliers, should-have-processed.
function buildEmailBuckets(result: EmailAccuracyAuditResult, t: TFn): ReviewBucket[] {
  const buckets: ReviewBucket[] = [];

  const wrong = result.processed.emails.filter(e => e.verdict === 'wrong');
  if (wrong.length > 0) {
    buckets.push({
      id: 'wrong', title: t('adminTesting.review.bucketWrongTotals'), hint: t('adminTesting.review.bucketWrongTotalsHint'),
      items: wrong.map(e => ({
        id: e.gmailMessageId,
        title: e.platform || (e.matchedIn === 'revenue' ? 'revenue' : '—'),
        subtitle: e.restaurantName ?? undefined,
        body: <EmailCardBody e={e} t={t} />,
        actions: (<><span className={cn(secondaryText, 'text-xs')}>{t('adminTesting.review.copyMsgId')}</span><MsgIdCopyCell rfc822MessageId={e.rfc822MessageId} label={e.rfc822MessageId || e.gmailMessageId} /></>),
      })),
    });
  }

  const needs = result.notProcessed.emails.filter(e => e.verdict === 'needs_check');
  const mkItem = (e: NotProcessedEmailResult) => ({
    id: e.gmailMessageId,
    title: e.from,
    subtitle: e.platform ?? undefined,
    body: <EmailCardBody e={e} t={t} />,
    actions: (<><span className={cn(secondaryText, 'text-xs')}>{t('adminTesting.review.copyMsgId')}</span><MsgIdCopyCell rfc822MessageId={e.rfc822MessageId} label={e.rfc822MessageId || e.gmailMessageId} /></>),
  });

  const newSuppliers = needs.filter(e => e.category === 'new_supplier');
  if (newSuppliers.length > 0) {
    buckets.push({ id: 'new', title: t('adminTesting.review.bucketNewSuppliers'), hint: t('adminTesting.review.bucketNewSuppliersHint'), items: newSuppliers.map(mkItem) });
  }
  const attention = needs.filter(e => e.category !== 'new_supplier');
  if (attention.length > 0) {
    buckets.push({ id: 'attention', title: t('adminTesting.review.bucketNeedsAttention'), hint: t('adminTesting.review.bucketNeedsAttentionHint'), items: attention.map(mkItem) });
  }

  return buckets;
}

export function EmailAccuracyAuditView({ result, t }: { result: EmailAccuracyAuditResult; t: TFn }) {
  const totalEmails = result.processed.total + result.notProcessed.total;
  const good = result.processed.correctCount + result.notProcessed.goodSkipCount;
  const scorePct = totalEmails > 0 ? (good / totalEmails) * 100 : 100;

  const fullBreakdown = (
    <div className="space-y-6">
      {/* Summary */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="bg-gray-200 dark:bg-gray-700 text-xs px-2 py-0.5 rounded-full font-medium">
            {t('adminTesting.accuracyAudit.summary.processed', { count: result.processed.total })}
          </span>
          {result.processed.wrongCount > 0 && (
            <span className="bg-red-600 dark:bg-red-600 text-white dark:text-white text-xs px-2 py-0.5 rounded-full font-medium">
              {t('adminTesting.accuracyAudit.summary.wrong', { count: result.processed.wrongCount })}
            </span>
          )}
          <span className="bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 text-xs px-2 py-0.5 rounded-full font-medium">
            {t('adminTesting.accuracyAudit.summary.notProcessed', { count: result.notProcessed.total })}
          </span>
          {result.notProcessed.needsCheckCount > 0 && (
            <span className="bg-red-600 dark:bg-red-600 text-white dark:text-white text-xs px-2 py-0.5 rounded-full font-medium">
              {t('adminTesting.accuracyAudit.summary.needsCheck', { count: result.notProcessed.needsCheckCount })}
            </span>
          )}
        </div>
        <span className={cn(secondaryText, 'text-xs font-mono')}>
          {t('adminTesting.accuracyAudit.cost', {
            cost: result.aiCost.totalCostEur.toFixed(4),
            sonnet: result.aiCost.sonnet.totalCostEur.toFixed(4),
            haiku: result.aiCost.haiku.totalCostEur.toFixed(4),
          })}
        </span>
      </div>

      {/* Messages already processed */}
      <div>
        <h4 className={cn(primaryText, semiBoldText, smallTextSize, 'mb-2')}>
          {t('adminTesting.accuracyAudit.processedHeading', {
            total: result.processed.total,
            correct: result.processed.correctCount,
            wrong: result.processed.wrongCount,
            notChecked: result.processed.notCheckedCount,
          })}
        </h4>
        {result.processed.emails.length === 0 ? (
          <div className={cn(secondaryText, smallTextSize)}>{t('adminTesting.accuracyAudit.noneProcessed')}</div>
        ) : (
          <div className="space-y-2">
            {result.processed.emails.map((e) => (
              <div
                key={e.gmailMessageId}
                className={cn(
                  'rounded-lg border p-3',
                  e.verdict === 'wrong'
                    ? 'border-red-200 dark:border-red-900/50 bg-red-50/40 dark:bg-red-900/10'
                    : 'border-gray-200 dark:border-gray-700'
                )}
              >
                {/* Meta line — wraps on small screens, never crushes the issues */}
                <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-xs">
                  <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-medium', PROCESSED_VERDICT_STYLES[e.verdict])}>
                    {t(`adminTesting.accuracyAudit.verdict.${e.verdict}`)}
                  </span>
                  <span className={cn(primaryText, 'font-mono')}>
                    <MsgIdCopyCell rfc822MessageId={e.rfc822MessageId} label={e.platform || (e.matchedIn === 'revenue' ? 'revenue' : '—')} />
                  </span>
                  {e.restaurantName && <span className={secondaryText}>{e.restaurantName}</span>}
                  {e.orderNumbers.length > 0 && (
                    <span className={cn(secondaryText)}>
                      <span className="uppercase tracking-wide text-[10px]">{t('adminTesting.accuracyAudit.col.orderNumbers')}</span>{' '}
                      <span className={cn(primaryText, 'font-mono')}>{e.orderNumbers.join(', ')}</span>
                    </span>
                  )}
                  {e.storedTotal != null && (
                    <span className={cn(secondaryText, 'ml-auto')}>
                      <span className="uppercase tracking-wide text-[10px]">{t('adminTesting.accuracyAudit.col.storedTotal')}</span>{' '}
                      <span className={cn(primaryText, 'font-mono')}>€{e.storedTotal.toFixed(2)}</span>
                    </span>
                  )}
                </div>
                {/* Issues — full width, easy to read */}
                {e.issues.length > 0 && (
                  <ul
                    className={cn(
                      'list-disc pl-5 mt-2 space-y-1 text-xs leading-relaxed',
                      e.verdict === 'wrong' ? 'text-red-700 dark:text-red-300' : secondaryText
                    )}
                  >
                    {e.issues.map((iss: string, i: number) => <li key={i}>{iss}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Messages not processed */}
      <div>
        <h4 className={cn(primaryText, semiBoldText, smallTextSize, 'mb-2')}>
          {t('adminTesting.accuracyAudit.notProcessedHeading', {
            total: result.notProcessed.total,
            needsCheck: result.notProcessed.needsCheckCount,
            goodSkip: result.notProcessed.goodSkipCount,
          })}
        </h4>
        {result.notProcessed.emails.length === 0 ? (
          <div className={cn(secondaryText, smallTextSize)}>{t('adminTesting.accuracyAudit.noneNotProcessed')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className={cn('border-b border-gray-200 dark:border-gray-700', secondaryText, 'uppercase tracking-wide')}>
                  <th className="py-2 pr-3">{t('adminTesting.accuracyAudit.col.verdict')}</th>
                  <th className="py-2 pr-3">{t('adminTesting.accuracyAudit.col.from')}</th>
                  <th className="py-2 pr-3">{t('adminTesting.accuracyAudit.col.subject')}</th>
                  <th className="py-2 pr-3">{t('adminTesting.accuracyAudit.col.reason')}</th>
                  <th className="py-2 pr-3">{t('adminTesting.accuracyAudit.col.source')}</th>
                  <th className="py-2 pr-3 text-right">{t('adminTesting.accuracyAudit.col.gmailId')}</th>
                </tr>
              </thead>
              <tbody>
                {result.notProcessed.emails.map((e) => (
                  <tr key={e.gmailMessageId} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-3">
                      <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-medium', NOT_PROCESSED_VERDICT_STYLES[e.verdict])}>
                        {t(`adminTesting.accuracyAudit.verdict.${e.verdict}`)}
                      </span>
                      <div className={cn(secondaryText, 'text-[10px] mt-0.5')}>{t(`adminTesting.accuracyAudit.category.${e.category}`)}</div>
                    </td>
                    <td className={cn('py-2 pr-3', secondaryText, 'max-w-[160px] truncate')} title={e.from}>{e.from}</td>
                    <td className={cn('py-2 pr-3', primaryText, 'max-w-[200px] truncate')} title={e.subject}>{e.subject}</td>
                    <td className={cn('py-2 pr-3', secondaryText, 'max-w-[260px]')}>{e.reason}</td>
                    <td className="py-2 pr-3">
                      <span className={cn(
                        'text-[10px] px-2 py-0.5 rounded-full font-medium',
                        e.source === 'ai'
                          ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      )}>
                        {t(`adminTesting.accuracyAudit.sourceLabel.${e.source}`)}
                      </span>
                    </td>
                    <td className={cn('py-2 pr-3 text-right', secondaryText, 'font-mono')}>
                      <MsgIdCopyCell rfc822MessageId={e.rfc822MessageId} label={e.rfc822MessageId || e.gmailMessageId} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <AuditScoreReport
      t={t}
      title={t('adminTesting.review.scoreTitleEmail', { account: result.account })}
      scorePct={scorePct}
      scannedLabel={t('adminTesting.review.scannedEmails', { count: totalEmails })}
      goodLabel={t('adminTesting.review.goodEmail', { correct: result.processed.correctCount, skip: result.notProcessed.goodSkipCount })}
      buckets={buildEmailBuckets(result, t)}
      costLabel={t('adminTesting.accuracyAudit.cost', {
        cost: result.aiCost.totalCostEur.toFixed(4),
        sonnet: result.aiCost.sonnet.totalCostEur.toFixed(4),
        haiku: result.aiCost.haiku.totalCostEur.toFixed(4),
      })}
      fullBreakdown={fullBreakdown}
    />
  );
}
